import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCarGallery } from './store/gallery-slice';
import {
  engineVideoUrlSet,
  fetchCarModel,
  getActiveTab,
  getCarStatus,
  getEngineVideoFilename,
  getFetchCarModelStatus,
  getModelId,
  getUserId,
  isFullScreenToggled,
  pushStartVideoUrlSet,
} from './store/car-slice';
import { carStatusSet } from './store/car-slice';
import { getError, getLoading } from './store/car-slice';
import { urlParam } from './utils/url-param';

import CAR360 from './pages/vdp/car-360';
import CarFeatures from './pages/car-features';
import MessageDisplay from './components/error-display/error-display';
import { handleDownloadFromGcs } from './services/gcs';
import VideoTour from './sections/video-tour/video-tour';
import Purchase from './sections/purchase/purchase';

export const Main = () => {
  const dispatch = useDispatch<any>();

  // Selectors
  const loading = useSelector(getLoading);
  const error = useSelector(getError);
  const fetchCarModelStatus = useSelector(getFetchCarModelStatus);

  const carStatus = useSelector(getCarStatus);
  const isVideosRequestedRef = React.useRef(false);

  const mountRef = React.useRef(false);
  const thumbnailRequestRef = React.useRef(false);

  const modelId = useSelector(getModelId);
  const userId = useSelector(getUserId);
  const engineVideoFilename = useSelector(getEngineVideoFilename);
  const activeTab = useSelector(getActiveTab);
  const pid = urlParam('pid');
  const mid = urlParam('mid');

  // to send request only once

  React.useEffect(() => {
    if (pid && !modelId) return;

    if (isVideosRequestedRef.current === false) {
      if (userId === undefined || modelId === undefined) return;

      const pushStartFilename: string = 'record-video/start.mp4';

      if (engineVideoFilename) {
        handleDownloadFromGcs(userId, modelId, engineVideoFilename)
          .then((url: any) => {
            dispatch(engineVideoUrlSet(url));
          })
          .catch((e) => console.log(e));
      }

      handleDownloadFromGcs(userId, modelId, pushStartFilename)
        .then((url: any) => {
          dispatch(pushStartVideoUrlSet(url));
        })
        .catch((e) => console.log(e));
    }
    isVideosRequestedRef.current = true;
  }, [userId, modelId, carStatus]);

  // check 'viewPage' query parameter in the URL if is exists only shows car page
  React.useEffect(() => {
    const viewPage = urlParam('viewPage');

    if (viewPage === 'car' || viewPage === 'admin') {
      dispatch(carStatusSet('CAR'));
    }
  }, []);

  React.useEffect(() => {
    if (pid && !mid) return;

    // if this api failed dont call other api
    if (mountRef.current === false) {
      if (fetchCarModelStatus === 'idle') {
        dispatch(fetchCarModel());
      }

      if (thumbnailRequestRef.current === false) {
        if (fetchCarModelStatus === 'fulfilled') {
          dispatch(fetchCarGallery('thumbnail')); // low quality
          thumbnailRequestRef.current = true;
        }
      }

      if (carStatus === 'CAR' && activeTab === 'gallery') {
        if (fetchCarModelStatus === 'fulfilled') {
          mountRef.current = true;
          // dispatch(fetchCarGallery(30)); // mid quality
          dispatch(fetchCarGallery(60)); // high quality
        }
      }
    }
  }, [fetchCarModelStatus, carStatus, activeTab, modelId]);

  // Function to handle fullscreen changes
  const handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
      console.log('Exited fullscreen mode');
      dispatch(isFullScreenToggled(false));
    } else {
      console.log('Entered fullscreen mode');
      dispatch(isFullScreenToggled(true));
    }
  };

  useEffect(() => {
    // Add event listeners for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange); // For Safari
    document.addEventListener('mozfullscreenchange', handleFullscreenChange); // For Firefox
    document.addEventListener('MSFullscreenChange', handleFullscreenChange); // For IE/Edge

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange,
      );
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  const renderCar = (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {modelId ? (
        <>
          <Purchase />
          <CAR360 />
          <CarFeatures />
        </>
      ) : null}
      {(modelId && carStatus === 'VDP' && pid) || (!modelId && pid) ? (
        <VideoTour />
      ) : null}
    </Box>
  );

  if (fetchCarModelStatus === 'rejected' || error) {
    return <MessageDisplay message={error} />;
  }

  const fixedContainer = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  };

  return (
    <Box component="main" sx={modelId && !pid ? fixedContainer : {}}>
      {loading ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'fixed',
            top: '50%',
            left: '50%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        renderCar
      )}
    </Box>
  );
};

export default Main;
